// Vaultavo Trust Centre — institutional content blocks.
// IMPORTANT: language must be honest about status — no overclaiming certifications.

export const PROGRAMME_STATUS = [
  {
    id: "information-security",
    title: "Information Security",
    status: "Programme Active",
    tone: "active",
    desc: "Formalised information security programme covering policies, controls, asset management, access control, and incident response.",
  },
  {
    id: "business-continuity",
    title: "Business Continuity",
    status: "Framework In Development",
    tone: "progress",
    desc: "Continuity and resilience framework under development to support institutional service expectations.",
  },
  {
    id: "regulatory-alignment",
    title: "Regulatory Alignment",
    status: "Ongoing",
    tone: "progress",
    desc: "Continuous monitoring of digital asset, custody, and financial services regulation across operating jurisdictions.",
  },
  {
    id: "privacy-data-protection",
    title: "Privacy & Data Protection",
    status: "Active",
    tone: "active",
    desc: "Privacy-by-design architecture. Biometric templates remain within the secure element — never transmitted, never stored externally.",
  },
  {
    id: "security-testing",
    title: "Security Testing",
    status: "Ongoing",
    tone: "progress",
    desc: "Recurring internal review and third-party assessment of hardware, firmware, cryptography, and application layers.",
  },
  {
    id: "vendor-risk",
    title: "Vendor & Supply Chain",
    status: "Under Review",
    tone: "progress",
    desc: "Manufacturing partners are evaluated for security posture, attestation, and chain-of-custody integrity.",
  },
];

export const CERTIFICATIONS = [
  {
    id: "iso-27001",
    name: "ISO 27001",
    family: "Information Security",
    status: "Underway",
    desc: "Information security management system certification.",
  },
  {
    id: "iso-22301",
    name: "ISO 22301",
    family: "Business Continuity",
    status: "Planned",
    desc: "Business continuity management system certification.",
  },
  {
    id: "soc-2",
    name: "SOC 2 Type II",
    family: "Operational Assurance",
    status: "Planned",
    desc: "Independent attestation against the AICPA Trust Services Criteria.",
  },
  {
    id: "ccss",
    name: "CCSS",
    family: "Cryptocurrency Security",
    status: "Underway",
    desc: "CryptoCurrency Security Standard for digital asset custody systems.",
  },
  {
    id: "dora",
    name: "DORA Readiness",
    family: "EU Operational Resilience",
    status: "Underway",
    desc: "Operational resilience alignment for digital finance entities in scope of the EU Digital Operational Resilience Act.",
  },
  {
    id: "eal6",
    name: "CC EAL6+",
    family: "Hardware Assurance",
    status: "Available Upon Request",
    desc: "Secure element used within the Vaultavo Biometric Security Device is independently certified to Common Criteria EAL6+ High.",
  },
];

export const ARCHITECTURE_LAYERS = [
  {
    id: "identity",
    label: "Layer 01",
    title: "Biometric Identity",
    desc: "On-device fingerprint matching within a Common Criteria EAL6+ High secure element. Biometric templates never leave the device — no cloud, no central database.",
    points: [
      "On-device matching",
      "Templates never transmitted",
      "Duress fingerprint policies",
    ],
  },
  {
    id: "hardware",
    label: "Layer 02",
    title: "Hardware Security",
    desc: "Hardware-segregated key custody. Each user's private keys are generated, stored, and used entirely within certified hardware — no shared zone keys, no pooled custody, no software exposure.",
    points: [
      "Per-user secure element isolation",
      "AES-256 DUKPT encrypted messaging",
      "Tamper-resistant architecture",
    ],
  },
  {
    id: "governance",
    label: "Layer 03",
    title: "Governance & Policy",
    desc: "Role-based controls, multi-party approval workflows, transaction limits, immutable audit trails, and policy-enforced compliance across the institution.",
    points: [
      "Initiator / verifier / approver",
      "Travel Rule & KYC hooks",
      "Cryptographic proof of intent",
    ],
  },
];

export const DOC_CATEGORIES = [
  {
    id: "security",
    title: "Security",
    items: [
      { name: "Security Whitepaper", status: "Coming Soon" },
      { name: "Architecture Overview", status: "Available Upon Request" },
      { name: "Incident Response Overview", status: "Available Upon Request" },
      { name: "Penetration Test Summary", status: "Available Upon Request" },
    ],
  },
  {
    id: "compliance",
    title: "Compliance",
    items: [
      { name: "DORA Information Pack", status: "Coming Soon" },
      { name: "Regulatory Alignment Overview", status: "Available Upon Request" },
      { name: "Governance Overview", status: "Available Upon Request" },
      { name: "AML / Travel Rule Summary", status: "Available Upon Request" },
    ],
  },
  {
    id: "privacy-legal",
    title: "Privacy & Legal",
    items: [
      { name: "Privacy Notice", status: "Available", href: "/privacy" },
      { name: "Cookie Policy", status: "Available", href: "/cookies" },
      { name: "Website Terms of Use", status: "Available", href: "/terms" },
      { name: "Data Processing Agreement", status: "Available Upon Request" },
    ],
  },
  {
    id: "operational",
    title: "Operational",
    items: [
      { name: "Business Continuity Overview", status: "Coming Soon" },
      { name: "Disaster Recovery Overview", status: "Coming Soon" },
      { name: "Service Availability Information", status: "Available Upon Request" },
    ],
  },
];

export const REQUEST_TYPES = [
  "Audit Request",
  "Due Diligence",
  "Security Review",
  "Procurement",
  "Compliance",
  "Other",
];

export const TRUST_FAQ = [
  {
    q: "Do you hold client funds?",
    a: "Vaultavo provides custody and security infrastructure — software, hardware, and governance — not a regulated balance sheet. Custody models supported include full self-custody, shared self-custody, institutional custody, and white-label deployment. In all models, private keys are generated and stored within certified secure hardware. Whether Vaultavo, an institution, or a user holds operational control is a deployment decision governed by policy.",
  },
  {
    q: "How is biometric data protected?",
    a: "Biometric templates are enrolled, stored, and matched entirely within the Common Criteria EAL6+ High secure element on the Vaultavo Biometric Security Device. Templates are never transmitted, never written to disk, and never stored in a cloud or central database. Matching occurs on the device itself — Vaultavo's backend never receives biometric data.",
  },
  {
    q: "What security certifications are planned?",
    a: "Vaultavo is actively progressing ISO 27001 (information security), ISO 22301 (business continuity), SOC 2 Type II, CCSS for digital asset custody, and operational resilience alignment under DORA. The secure element within the device is already independently certified to Common Criteria EAL6+ High. Status of each track is published in the Certifications section above.",
  },
  {
    q: "How do I request documentation?",
    a: "Use the Documentation Requests form on this page. Specify the document type and your purpose (audit, due diligence, procurement, security review). Sensitive materials are released under a mutual NDA. Standard turnaround is five business days for documents marked Available Upon Request.",
  },
  {
    q: "How does Vaultavo support regulatory compliance?",
    a: "Vaultavo's architecture is designed to align with the security, auditability, governance, and reporting expectations of regulated financial institutions. Features include immutable audit trails, role-based and multi-party approval workflows, KYC and Travel Rule integration hooks, segregated key custody, and cryptographic proof of authorisation. Vaultavo also maintains active monitoring of evolving regulation across operating jurisdictions.",
  },
  {
    q: "What custody models are supported?",
    a: "Vaultavo supports full self-custody, shared self-custody, institutional custody, white-label custody under an institution's brand, on-premises deployment within institutional infrastructure, and corporate treasury management with multi-user approval workflows. All models share the same hardware-secured, biometrically authorised architecture.",
  },
  {
    q: "Is Vaultavo subject to penetration testing?",
    a: "Yes. Vaultavo's hardware, firmware, cryptographic operations, and application layers are subject to recurring internal review and independent third-party assessment. Executive summaries of recent assessments are available upon request under NDA.",
  },
  {
    q: "Where is data hosted?",
    a: "Application and operational data is hosted across hardened, ISO-aligned cloud infrastructure in primary and secondary regions. No biometric data is ever transmitted to or stored on backend infrastructure. Detailed hosting architecture is available upon request.",
  },
];
